/**
 * "How did you hear about us?" (migration 0073).
 *
 * WHY THIS EXISTS AT ALL
 *   It is not a soft alternative to real attribution: for an iOS App Store
 *   install it is the ONLY attribution there is. Apple passes no referrer,
 *   fingerprint matching is against store rules, and AdServices covers only
 *   Apple's own paid ads. A social short driving iOS installs is unmeasurable
 *   by every other means available to us.
 *
 * WHY IT IS SO SMALL
 *   It sits in the trainer's way for one tap, so it asks one question, offers
 *   fixed answers, and never comes back. Anything more elaborate would cost
 *   more goodwill than the answer is worth, and a prompt people resent is a
 *   prompt they lie to.
 *
 * WHEN (lib/firstRunPrompts, 2026-10-23): never on the first session, which
 * belongs to the tour; from the second, and only when no other ask has had
 * this session. Shown once per device, and never when the account already
 * carries an answer (`heardAboutAnswered`, 2026-09-16): a reinstall or a
 * second phone cleared the per-device flag and re-asked every existing
 * trainer. The server writes only when the column is still null, so a cleared
 * localStorage cannot overwrite an earlier honest answer with a later guess.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/components/AuthProvider";
import { useTrainerStepKind } from "@/lib/useTrainerStep";
import { useFirstRunPromptDue } from "@/lib/useFirstRunPrompt";
import { claimFirstRunPrompt, markFirstRunPromptDone } from "@/lib/firstRunPrompts";
import { apiRequestRaw } from "@/lib/queryClient";
import { HEARD_ABOUT_OPTIONS } from "@/lib/acquisition";

export function HeardAboutPrompt(): JSX.Element | null {
  const { account } = useAuth();
  // Yields to the trainer step like the other timed prompts: since 2026-10-02
  // that step opens with the app for an account that has not typed its name,
  // and this card surfaced on top of its scrim 20 seconds later.
  const trainerStepOpen = useTrainerStepKind(account) != null;
  const due = useFirstRunPromptDue("heard-about");
  const [visible, setVisible] = useState(false);

  // Already answered on another device: remember that here too, so the
  // schedule never offers this card its turn.
  const answeredElsewhere = account?.heardAboutAnswered === true;
  useEffect(() => {
    if (answeredElsewhere) markFirstRunPromptDone("heard-about");
  }, [answeredElsewhere]);

  useEffect(() => {
    if (!due) return;
    // Deliberately delayed. Asking the instant they land interrupts whatever
    // they opened the app to do, which is both rude and the fastest way to get
    // a random answer.
    const timer = setTimeout(() => {
      claimFirstRunPrompt("heard-about");
      setVisible(true);
    }, 20_000);
    return () => clearTimeout(timer);
  }, [due]);

  const answer = (key: string | null) => {
    setVisible(false);
    markFirstRunPromptDone("heard-about");
    if (key == null) return; // dismissed without answering
    void apiRequestRaw("POST", "/api/account/heard-about", { answer: key }).catch(() => {
      /* never surface: this is our question, not their problem */
    });
  };

  if (!visible || trainerStepOpen) return null;

  return (
    <div
      className="fixed left-3 right-3 z-50 rounded-xl p-3"
      style={{
        // Clear of the Active Bonuses chip too (bottom 84px, ~36px tall, lower-left
        // on Profile): the review card was landing on it (owner, 2026-09-16).
        bottom: "calc(var(--nx-safe-bottom) + 132px)",
        background: "var(--surface-overlay)",
        border: "1px solid var(--accent-fill-glow-subtle)",
        boxShadow: "0 8px 30px rgba(0,0,0,0.28)",
        maxWidth: 460,
        marginInline: "auto",
      }}
      data-testid="heard-about-prompt"
    >
      <div className="flex items-start gap-2">
        <p className="text-[13px] font-semibold flex-1">How did you find Nexus Dex?</p>
        <button
          type="button"
          onClick={() => answer(null)}
          className="text-[11px] px-1"
          style={{ color: "var(--text-tertiary)" }}
          aria-label="Dismiss"
          data-testid="heard-about-dismiss"
        >
          ✕
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {HEARD_ABOUT_OPTIONS.map((o) => (
          <button
            key={o.key}
            type="button"
            onClick={() => answer(o.key)}
            className="text-[12px] px-2.5 py-1 rounded-md border transition hover:opacity-80"
            style={{ borderColor: "var(--accent-fill-glow-subtle)", color: "var(--text-secondary)" }}
            data-testid={`heard-about-${o.key}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
